import { useEffect, useState, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { QRCodeSVG } from "qrcode.react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  MapPin,
  Phone,
  Globe,
  ExternalLink,
  ArrowLeft,
  Store,
  Crown,
  CheckCircle2,
  Tag,
  Clock,
  Mail,
  MessageCircle,
  Sparkles,
  Loader2,
  AlertTriangle,
  Heart,
  Star,
  LayoutDashboard,
  Trash2,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  restaurantes: "Restaurantes",
  hoteles: "Hoteles",
  cafes: "Cafés",
  bares: "Bares",
  tours: "Tours",
  car_wash: "Car Wash",
  mecanicos: "Mecánicos",
  salud_belleza: "Salud y Belleza",
  entretenimiento: "Entretenimiento",
  otros: "Otros",
};

const DAY_LABELS = {
  lunes: "Lun", martes: "Mar", miercoles: "Mié",
  jueves: "Jue", viernes: "Vie", sabado: "Sáb", domingo: "Dom",
};

function statusLabel(s) {
  return {
    pending: "Pendiente de aprobación",
    approved: "Activo",
    rejected: "Rechazado",
    suspended: "Suspendido",
    paused: "Pausado por el dueño",
  }[s] || s;
}

function StarRating({ value, size = 18, onChange = null, testIdPrefix = "star" }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={`${testIdPrefix}-rating`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          disabled={!onChange}
          onClick={() => onChange?.(n)}
          className={`${onChange ? "cursor-pointer" : "cursor-default"}`}
          data-testid={`${testIdPrefix}-${n}`}
        >
          <Star
            style={{ width: size, height: size }}
            className={
              n <= value ? "text-amber-400 fill-amber-400" : "text-neutral-300"
            }
          />
        </button>
      ))}
    </div>
  );
}

export default function GuiaBusinessDetailPage() {
  const { businessId } = useParams();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [biz, setBiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activePhoto, setActivePhoto] = useState(0);

  // Phase 2: claim
  const [claimedCoupon, setClaimedCoupon] = useState(null);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [claiming, setClaiming] = useState(false);

  // Favorites
  const [isFavorite, setIsFavorite] = useState(false);
  const [favBusy, setFavBusy] = useState(false);

  // Reviews
  const [reviews, setReviews] = useState([]);
  const [ratingAvg, setRatingAvg] = useState(null);
  const [ratingCount, setRatingCount] = useState(0);
  const [myReview, setMyReview] = useState({ rating: 5, comment: "" });
  const [reviewSubmitting, setReviewSubmitting] = useState(false);

  const loadReviews = useCallback(async () => {
    try {
      const { data } = await api.get(`/guia/businesses/${businessId}/reviews`);
      setReviews(data.items || []);
      setRatingAvg(data.rating_avg);
      setRatingCount(data.rating_count || 0);
      // Pre-fill my existing review
      const mine = (data.items || []).find((r) => r.user_id === user?.user_id);
      if (mine) {
        setMyReview({ rating: mine.rating, comment: mine.comment || "" });
      }
    } catch {
      /* ignore */
    }
  }, [businessId, user?.user_id]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/guia/businesses/${businessId}`);
        if (!cancelled) setBiz(data);
      } catch (err) {
        if (!cancelled) setError(err.response?.status === 404 ? "Negocio no encontrado" : "Error al cargar");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [businessId]);

  useEffect(() => {
    if (!biz || biz.status !== "approved") return;
    loadReviews();
  }, [biz, loadReviews]);

  // Check if this business is favorited
  useEffect(() => {
    if (!isAuthenticated || !biz || biz.status !== "approved") return;
    let cancelled = false;
    api
      .get("/guia/favorites")
      .then(({ data }) => {
        if (!cancelled) {
          setIsFavorite((data.items || []).some((b) => b.business_id === businessId));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, biz, businessId]);

  const claimDiscount = async () => {
    if (!isAuthenticated) {
      toast.info("Inicia sesión para reclamar el descuento");
      navigate("/login", { state: { from: `/guia/${businessId}` } });
      return;
    }
    setClaiming(true);
    try {
      const { data } = await api.post(`/guia/businesses/${businessId}/claim`);
      setClaimedCoupon(data);
      setShowCouponDialog(true);
      toast.success("¡Descuento reclamado!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo reclamar el descuento");
    } finally {
      setClaiming(false);
    }
  };

  const toggleFavorite = async () => {
    if (!isAuthenticated) {
      toast.info("Inicia sesión para guardar favoritos");
      navigate("/login", { state: { from: `/guia/${businessId}` } });
      return;
    }
    setFavBusy(true);
    try {
      if (isFavorite) {
        await api.delete(`/guia/favorites/${businessId}`);
        setIsFavorite(false);
      } else {
        await api.post(`/guia/favorites/${businessId}`);
        setIsFavorite(true);
      }
    } catch {
      toast.error("No se pudo actualizar favoritos");
    } finally {
      setFavBusy(false);
    }
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate("/login", { state: { from: `/guia/${businessId}` } });
      return;
    }
    setReviewSubmitting(true);
    try {
      await api.post(`/guia/businesses/${businessId}/reviews`, {
        rating: myReview.rating,
        comment: myReview.comment,
      });
      toast.success("¡Reseña enviada!");
      await loadReviews();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo enviar la reseña");
    } finally {
      setReviewSubmitting(false);
    }
  };

  const deleteMyReview = async (reviewId) => {
    if (!window.confirm("¿Eliminar tu reseña?")) return;
    try {
      await api.delete(`/guia/reviews/${reviewId}`);
      setMyReview({ rating: 5, comment: "" });
      await loadReviews();
      toast.success("Reseña eliminada");
    } catch {
      toast.error("No se pudo eliminar la reseña");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center" data-testid="biz-detail-loading">
        <Loader2 className="w-8 h-8 animate-spin text-terracotta" />
      </div>
    );
  }

  if (error || !biz) {
    return (
      <div className="min-h-screen bg-sand flex items-center justify-center p-6" data-testid="biz-detail-error">
        <div className="card-base text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-3" />
          <h2 className="font-heading text-xl text-agave mb-2">{error || "Negocio no encontrado"}</h2>
          <Link to="/guia" className="btn-primary inline-flex mt-2">Volver a la Guía</Link>
        </div>
      </div>
    );
  }

  const photos = biz.photos?.length ? biz.photos : (biz.logo_url ? [biz.logo_url] : []);
  const isNotApproved = biz.status && biz.status !== "approved";
  const isOwner = user?.user_id && biz.owner_user_id && user.user_id === biz.owner_user_id;
  const existingReview = reviews.find((r) => r.user_id === user?.user_id);

  return (
    <div className="min-h-screen bg-sand" data-testid="biz-detail-page">
      {/* Photo gallery */}
      <section className="bg-black">
        <div className="container-main">
          {photos.length > 0 ? (
            <>
              <div className="relative aspect-[16/9] sm:aspect-[21/9] overflow-hidden">
                <img
                  src={photos[activePhoto]}
                  alt={biz.name}
                  className="w-full h-full object-cover"
                />
                {biz.discount_percent ? (
                  <div className="absolute top-4 right-4 bg-terracotta text-white text-lg font-bold px-4 py-2 rounded-full shadow-xl">
                    -{Math.round(biz.discount_percent)}%
                  </div>
                ) : null}
                {/* Favorite button overlay */}
                {biz.status === "approved" && (
                  <button
                    onClick={toggleFavorite}
                    disabled={favBusy}
                    className={`absolute top-4 left-4 w-11 h-11 rounded-full backdrop-blur bg-white/30 hover:bg-white/50 flex items-center justify-center transition-all ${
                      isFavorite ? "text-rose-500" : "text-white"
                    }`}
                    data-testid="biz-fav-btn"
                    aria-label="Favorito"
                  >
                    <Heart className={`w-6 h-6 ${isFavorite ? "fill-rose-500" : ""}`} />
                  </button>
                )}
              </div>
              {photos.length > 1 && (
                <div className="flex gap-2 p-2 overflow-x-auto scrollbar-hide" data-testid="biz-detail-gallery">
                  {photos.map((p, i) => (
                    <button
                      key={p + i}
                      onClick={() => setActivePhoto(i)}
                      className={`relative w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 ${
                        activePhoto === i ? "ring-2 ring-white" : "opacity-70"
                      }`}
                      data-testid={`biz-detail-thumb-${i}`}
                    >
                      <img src={p} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="aspect-[16/9] flex items-center justify-center text-neutral-700 bg-neutral-200">
              <Store className="w-16 h-16" />
            </div>
          )}
        </div>
      </section>

      <div className="container-main py-6 max-w-4xl">
        <Link
          to="/guia"
          className="inline-flex items-center gap-2 text-sm text-neutral-600 hover:text-agave mb-4"
          data-testid="biz-detail-back"
        >
          <ArrowLeft className="w-4 h-4" />
          Volver a la Guía
        </Link>

        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 text-xs text-neutral-500 mb-1">
              <Tag className="w-3 h-3" />
              {CATEGORY_LABELS[biz.category] || biz.category}
            </div>
            <h1 className="text-3xl font-heading font-bold text-agave">{biz.name}</h1>
            <div className="flex items-center gap-2 text-neutral-600 mt-1">
              <MapPin className="w-4 h-4 text-terracotta" />
              <span>{[biz.address, biz.city, biz.state].filter(Boolean).join(" · ")}</span>
            </div>
            {/* Rating summary */}
            {ratingCount > 0 && (
              <div className="flex items-center gap-2 mt-2" data-testid="biz-rating-summary">
                <StarRating value={Math.round(ratingAvg || 0)} testIdPrefix="biz-detail-star" />
                <span className="text-sm font-medium text-agave">{(ratingAvg || 0).toFixed(1)}</span>
                <span className="text-xs text-neutral-500">({ratingCount} reseñas)</span>
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {isOwner && (
              <Link
                to={`/guia/${biz.business_id}/dashboard`}
                className="btn-secondary inline-flex items-center gap-1 text-sm"
                data-testid="biz-owner-dashboard-link"
              >
                <LayoutDashboard className="w-4 h-4" />
                Panel
              </Link>
            )}
            {biz.featured && (
              <Badge className="bg-amber-500 text-white border-0 gap-1">
                <Crown className="w-3 h-3" />
                Destacado
              </Badge>
            )}
            {biz.verified_partner && (
              <Badge className="bg-emerald-600 text-white border-0 gap-1">
                <CheckCircle2 className="w-3 h-3" />
                Socio verificado
              </Badge>
            )}
            {isNotApproved && (
              <Badge className="bg-amber-100 text-amber-800 border-amber-200">
                {statusLabel(biz.status)}
              </Badge>
            )}
          </div>
        </div>

        {isNotApproved && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 text-sm" data-testid="biz-pending-banner">
            <p className="font-medium text-amber-900 mb-1">Vista previa privada</p>
            <p className="text-amber-800">
              Este listado aún no es público. Solo tú (y el equipo de Rivones) lo pueden ver hasta que sea aprobado.
            </p>
            {biz.rejection_reason && (
              <p className="text-red-700 mt-2"><strong>Motivo:</strong> {biz.rejection_reason}</p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left column */}
          <div className="lg:col-span-2 space-y-6">
            <section className="card-base">
              <h2 className="font-heading font-semibold text-lg text-agave mb-2">Acerca de</h2>
              <p className="text-neutral-700 whitespace-pre-line">{biz.description}</p>
            </section>

            {(biz.discount_details || biz.discount_percent) && (
              <section className="card-base bg-gradient-to-br from-terracotta/5 to-amber-50 border-terracotta/20" data-testid="biz-discount-section">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-5 h-5 text-terracotta" />
                  <h2 className="font-heading font-semibold text-lg text-agave">
                    Descuento exclusivo para miembros RivoNes
                  </h2>
                </div>
                {biz.discount_percent ? (
                  <p className="text-3xl font-bold text-terracotta mb-2">
                    -{Math.round(biz.discount_percent)}%
                  </p>
                ) : null}
                {biz.discount_details && (
                  <p className="text-neutral-800 mb-3">{biz.discount_details}</p>
                )}
                {biz.discount_terms && (
                  <p className="text-xs text-neutral-600 italic">
                    <strong>Términos:</strong> {biz.discount_terms}
                  </p>
                )}
                {biz.discount_expires_at && (
                  <p className="text-xs text-neutral-500 mt-2">
                    Válido hasta: {new Date(biz.discount_expires_at).toLocaleDateString("es-MX")}
                  </p>
                )}
                {!isOwner && biz.status === "approved" && (
                  <Button
                    onClick={claimDiscount}
                    disabled={claiming}
                    className="mt-4 bg-terracotta hover:bg-terracotta/90 text-white"
                    data-testid="biz-claim-btn"
                  >
                    {claiming ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                        Reclamando…
                      </>
                    ) : (
                      "Reclamar descuento"
                    )}
                  </Button>
                )}
                {isOwner && (
                  <p className="mt-4 text-xs text-neutral-500 italic">
                    Tú eres el dueño — comparte tu listado para que los miembros lo reclamen.
                  </p>
                )}
              </section>
            )}

            {biz.hours && Object.keys(biz.hours).length > 0 && (
              <section className="card-base" data-testid="biz-hours-section">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-5 h-5 text-terracotta" />
                  <h2 className="font-heading font-semibold text-lg text-agave">Horarios</h2>
                </div>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  {Object.entries(biz.hours).map(([day, range]) => (
                    <li key={day} className="flex justify-between border-b border-dashed border-neutral-200 py-1">
                      <span className="font-medium text-neutral-700">{DAY_LABELS[day] || day}</span>
                      <span className="text-neutral-600">{range}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Reviews */}
            {biz.status === "approved" && (
              <section id="reviews" className="card-base" data-testid="biz-reviews-section">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading font-semibold text-lg text-agave flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Reseñas ({ratingCount})
                  </h2>
                </div>

                {/* Write review (members only, not owner) */}
                {isAuthenticated && !isOwner && (
                  <form onSubmit={submitReview} className="mb-6 p-4 bg-amber-50 rounded-xl border border-amber-100" data-testid="biz-review-form">
                    <p className="font-medium text-sm mb-2">
                      {existingReview ? "Edita tu reseña" : "Deja tu reseña"}
                    </p>
                    <div className="mb-3">
                      <StarRating
                        value={myReview.rating}
                        onChange={(v) => setMyReview((m) => ({ ...m, rating: v }))}
                        size={26}
                        testIdPrefix="biz-my-star"
                      />
                    </div>
                    <Textarea
                      value={myReview.comment}
                      onChange={(e) => setMyReview((m) => ({ ...m, comment: e.target.value }))}
                      placeholder="Comparte tu experiencia… (opcional)"
                      maxLength={1000}
                      rows={3}
                      data-testid="biz-review-textarea"
                    />
                    <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                      <p className="text-xs text-neutral-500">{myReview.comment.length}/1000</p>
                      <div className="flex gap-2">
                        {existingReview && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-red-600"
                            onClick={() => deleteMyReview(existingReview.review_id)}
                            data-testid="biz-review-delete-btn"
                          >
                            <Trash2 className="w-4 h-4 mr-1" />
                            Eliminar
                          </Button>
                        )}
                        <Button
                          type="submit"
                          disabled={reviewSubmitting || !myReview.rating}
                          className="btn-primary"
                          data-testid="biz-review-submit-btn"
                        >
                          {reviewSubmitting
                            ? "Enviando…"
                            : existingReview
                            ? "Actualizar reseña"
                            : "Publicar reseña"}
                        </Button>
                      </div>
                    </div>
                  </form>
                )}
                {!isAuthenticated && (
                  <p className="text-sm text-neutral-600 mb-4">
                    <Link to="/login" state={{ from: `/guia/${businessId}` }} className="text-terracotta font-medium hover:underline">
                      Inicia sesión
                    </Link>{" "}
                    para dejar una reseña.
                  </p>
                )}

                {/* Reviews list */}
                {reviews.length === 0 ? (
                  <p className="text-sm text-neutral-500 text-center py-6">
                    Sé el primero en reseñar este negocio.
                  </p>
                ) : (
                  <div className="space-y-4">
                    {reviews.map((r) => (
                      <div
                        key={r.review_id}
                        className="flex items-start gap-3 pb-4 border-b border-neutral-100 last:border-0 last:pb-0"
                        data-testid={`biz-review-${r.review_id}`}
                      >
                        {r.user_picture ? (
                          <img src={r.user_picture} alt="" className="w-10 h-10 rounded-full object-cover flex-shrink-0" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-neutral-200 flex-shrink-0" />
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium text-sm text-agave">{r.user_name}</p>
                            <StarRating value={r.rating} size={14} testIdPrefix={`rv-${r.review_id}-star`} />
                            <span className="text-xs text-neutral-400">
                              {new Date(r.created_at).toLocaleDateString("es-MX")}
                            </span>
                          </div>
                          {r.comment && <p className="text-sm text-neutral-700 mt-1">{r.comment}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}
          </div>

          {/* Right column — contact actions */}
          <aside className="space-y-3">
            {biz.whatsapp && (
              <a
                href={`https://wa.me/${biz.whatsapp.replace(/[^0-9]/g, "")}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card-base flex items-center gap-3 hover:border-emerald-500 transition-colors"
                data-testid="biz-whatsapp"
              >
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 text-emerald-600 flex items-center justify-center">
                  <MessageCircle className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">WhatsApp</p>
                  <p className="text-xs text-neutral-500 truncate">{biz.whatsapp}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
            )}
            {biz.phone && (
              <a
                href={`tel:${biz.phone}`}
                className="card-base flex items-center gap-3 hover:border-terracotta transition-colors"
                data-testid="biz-phone"
              >
                <div className="w-10 h-10 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center">
                  <Phone className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">Llamar</p>
                  <p className="text-xs text-neutral-500 truncate">{biz.phone}</p>
                </div>
              </a>
            )}
            {biz.maps_link && (
              <a
                href={biz.maps_link}
                target="_blank"
                rel="noopener noreferrer"
                className="card-base flex items-center gap-3 hover:border-blue-500 transition-colors"
                data-testid="biz-maps"
              >
                <div className="w-10 h-10 rounded-xl bg-blue-500/10 text-blue-600 flex items-center justify-center">
                  <MapPin className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">Cómo llegar</p>
                  <p className="text-xs text-neutral-500 truncate">Google Maps</p>
                </div>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
            )}
            {biz.website && (
              <a
                href={biz.website.startsWith("http") ? biz.website : `https://${biz.website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="card-base flex items-center gap-3 hover:border-violet-500 transition-colors"
                data-testid="biz-website"
              >
                <div className="w-10 h-10 rounded-xl bg-violet-500/10 text-violet-600 flex items-center justify-center">
                  <Globe className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">Sitio web</p>
                  <p className="text-xs text-neutral-500 truncate">{biz.website}</p>
                </div>
                <ExternalLink className="w-4 h-4 text-neutral-400" />
              </a>
            )}
            {biz.email && (
              <a
                href={`mailto:${biz.email}`}
                className="card-base flex items-center gap-3 hover:border-neutral-700 transition-colors"
                data-testid="biz-email"
              >
                <div className="w-10 h-10 rounded-xl bg-neutral-200 text-neutral-700 flex items-center justify-center">
                  <Mail className="w-5 h-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-agave">Email</p>
                  <p className="text-xs text-neutral-500 truncate">{biz.email}</p>
                </div>
              </a>
            )}
          </aside>
        </div>
      </div>

      {/* Coupon Claimed Dialog (Phase 2) */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md" data-testid="coupon-claimed-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-amber-500" />
              ¡Descuento reclamado!
            </DialogTitle>
            <DialogDescription>
              Muestra este código (o el QR) al negocio para canjear tu descuento.
            </DialogDescription>
          </DialogHeader>
          {claimedCoupon && (
            <div className="space-y-4">
              <div className="bg-neutral-900 text-white rounded-xl p-4 text-center">
                <p className="text-xs uppercase tracking-wider text-white/60 mb-1">Tu código</p>
                <p className="text-4xl font-mono font-bold tracking-widest" data-testid="claimed-coupon-code">
                  {claimedCoupon.code}
                </p>
              </div>
              <div className="flex justify-center p-4 bg-white border rounded-xl">
                <QRCodeSVG value={claimedCoupon.qr_token} size={200} level="M" includeMargin />
              </div>
              {claimedCoupon.discount_percent ? (
                <p className="text-center text-2xl font-bold text-terracotta">
                  -{Math.round(claimedCoupon.discount_percent)}%
                </p>
              ) : null}
              {claimedCoupon.discount_details && (
                <p className="text-sm text-neutral-700 text-center">{claimedCoupon.discount_details}</p>
              )}
              {claimedCoupon.expires_at && (
                <p className="text-xs text-neutral-500 text-center">
                  Válido hasta {new Date(claimedCoupon.expires_at).toLocaleDateString("es-MX")}
                </p>
              )}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setShowCouponDialog(false)}
                  data-testid="claimed-coupon-close"
                >
                  Cerrar
                </Button>
                <Link
                  to="/guia/mis-cupones"
                  className="flex-1 btn-primary inline-flex items-center justify-center"
                  data-testid="claimed-coupon-view-all"
                >
                  Ver mis cupones
                </Link>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
